<template lang="pug">
.plans
  img.plans__decoration(
    :src="'/svg/pricing/Background.svg'"
  )
  .lp-container
    .plans__wrapper
      // ----
      // Single pricing plan
      .plan.plan_empty-price
        .plan__wrapper
          .plan__headline
            HandIconSvg.plan__headline-icon
            h3.plan__headline-title {{ $t('pages.ssr_pricing_plans.plans.non_profit.name') }}

          .plan__price
            template
              span.plan__price-duration {{ $t('single_words.from').toLowerCase() }}
              span.plan__price-value(v-html="'&nbsp;' + currencySymbol + ' 4.99&nbsp;'")
              span.plan__price-duration /{{ $t('single_words.month').toLowerCase() }}

          .plan__description
            p.plan__description-text(
              v-html="$t('pages.ssr_pricing_plans.plans.non_profit.text')"
            )
          .plan__billing
            ul.plan__features
              li.plan__features-item(
                v-for="f in nonProfitPlanFeatures"
                :key="'non-profit-' + f.key"
                :class="{'plan__features-item_unlimited': listOfFeatures[f].unlimited, 'plan__features-item_tooltip': listOfFeatures[f].tooltip, 'plan__features-item_tooltip-narrow': listOfFeatures[f].tooltipNarrow}"
              )
                InfiniteIcon.plan__features-item-infinite(v-if="listOfFeatures[f].unlimited")
                template {{ $t('pages.ssr_pricing_plans.plans.features.' + listOfFeatures[f].key + '.text') }}
                InfoCircle.plan__features-item-info(
                  v-if="listOfFeatures[f].tooltip"
                  :animated="false"
                  neat
                  html
                  multilined
                  narrow-icon
                  :autoResponsive="true"
                  :text="$t('pages.ssr_pricing_plans.plans.features.' + listOfFeatures[f].key + '.tooltip', { url: `https://help.rss.com/${$i18n.locale}/support/solutions/articles/44002307294-bandwidth-on-rss-com` })"
                )
          RssUiButton.plan__button(
            wide
            rainbow
            :blank="false"
            :href="getStartedForFreeButtonLink"
          ) {{ getStartedForFreeButtonText }}

      // ----
      // Single pricing plan
      .plan.plan_primary.plan_first
        transition(
          name="vuejs-fade-animation"
        )
          .plan__discount(
            v-if="proBillingAnnually"
          ) {{discountOfAnnualAllInOnePlan}}% {{ $t('sentences.discount_applied') }}
        .plan__wrapper
          .plan__headline
            MicSvg1.plan__headline-icon
            h3.plan__headline-title {{ $t('pages.ssr_pricing_plans.plans.all_in_one.name') }}
          .plan__price(
            :class="{'plan__price_annually': proBillingAnnually}"
          )
            span.plan__price-value.plan__price-value_stroked(
              v-if="proBillingAnnually"
              v-html="currencySymbol + ' 15.99 '"
            )
            span.plan__price-value(v-html="allInOnePrice")
            span.plan__price-duration /{{ $t('single_words.month').toLowerCase() }}
            span.plan__price-annually(
              v-if="proBillingAnnually"
            ) {{ $t('pages.ssr_pricing_plans.plans.all_in_one.if_annually') }}
          .plan__description
            p.plan__description-text(v-html="$t('pages.ssr_pricing_plans.plans.all_in_one.text')")
          .plan__billing
            transition(
              name="vuejs-fade-animation"
            )
              .plan__billing-annual.lp-headline-subtitle(
                v-if="!proBillingAnnually"
              ) {{ $t('pages.ssr_pricing_plans.plans.all_in_one.select_annual', { discount: discountOfAnnualAllInOnePlan }) }}
              .plan__billing-annual.lp-headline-subtitle(
                v-else
              ) {{ computedDiscountAppliedAllInOneText }}
            .plan__billing-bottom
              span.plan__billing-text {{ $t('single_words.billing') }}: {{ $t('single_words.monthly') }}
              BSwitch(v-model="proBillingAnnually")
              span.plan__billing-text {{ $t('single_words.annual') }}
            RssUiButton.plan__billing-btn.plan__button(
              wide
              rainbow
              :blank="false"
              :href="getStartedForFreeButtonLink"
            ) {{ getStartedForFreeButtonText }}

            ul.plan__features
              li.plan__features-item(
                v-for="f in allInOnePlanFeatures"
                :key="'non-profit-' + f.key"
                :class="{'plan__features-item_unlimited': listOfFeatures[f].unlimited, 'plan__features-item_tooltip': listOfFeatures[f].tooltip, 'plan__features-item_tooltip-narrow': listOfFeatures[f].tooltipNarrow}"
              )
                InfiniteIcon.plan__features-item-infinite(v-if="listOfFeatures[f].unlimited")
                template {{ $t('pages.ssr_pricing_plans.plans.features.' + listOfFeatures[f].key + '.text') }}
                InfoCircle.plan__features-item-info(
                  v-if="listOfFeatures[f].tooltip"
                  :animated="false"
                  html
                  neat
                  narrow-icon
                  multilined
                  :autoResponsive="true"
                  :text="$t('pages.ssr_pricing_plans.plans.features.' + listOfFeatures[f].key + '.tooltip', { url: `https://help.rss.com/${$i18n.locale}/support/solutions/articles/44002307294-bandwidth-on-rss-com` })"
                )

          RssUiButton.plan__button(
            wide
            rainbow
            :blank="false"
            :href="getStartedForFreeButtonLink"
          ) {{ getStartedForFreeButtonText }}

          span.plans__no-card {{ $t('pages.ssr_pricing_plans.plans.card_info') }}

      // ----
      // Single pricing plan
      .plan.plan_auto-height
        .plan__wrapper
          .plan__headline
            MicSvg2.plan__headline-icon
            h3.plan__headline-title {{ $t('pages.ssr_pricing_plans.plans.enterprise.name') }}
          .plan__price(
            :class="{'plan__price_annually': networksBillingAnnually}"
          )
            span.plan__price-value.plan__price-value_stroked(
              v-if="networksBillingAnnually"
              v-html="currencySymbol + ' 24.99 '"
            )
            span.plan__price-value(v-html="podcastNetworksPrice")
            span.plan__price-duration /{{ $t('single_words.month').toLowerCase() }}
            span.plan__price-annually(
              v-if="networksBillingAnnually"
            ) {{ $t('pages.ssr_pricing_plans.plans.all_in_one.if_annually') }}
          .plan__description
            p.plan__description-text(v-html="$t('pages.ssr_pricing_plans.plans.podcast_networks.text')")
          .plan__billing
            transition(
              name="vuejs-fade-animation"
            )
              .plan__billing-annual.lp-headline-subtitle(
                v-if="!networksBillingAnnually"
              ) {{ $t('pages.ssr_pricing_plans.plans.all_in_one.select_annual', { discount: discountOfAnnualPodcastNetworksPlan }) }}
              .plan__billing-annual.lp-headline-subtitle(
                v-else
              ) {{ computedDiscountAppliedPodcastNetworksText }}
            .plan__billing-bottom
              span.plan__billing-text {{ $t('single_words.billing') }}: {{ $t('single_words.monthly') }}
              BSwitch(v-model="networksBillingAnnually")
              span.plan__billing-text {{ $t('single_words.annual') }}
            RssUiButton.plan__billing-btn.plan__button(
              wide
              rainbow
              :blank="false"
              :href="getStartedForFreeButtonLink"
            ) {{ getStartedForFreeButtonText }}
            ul.plan__features
              li.plan__features-item(
                v-for="f in enterprisePlanFeatures"
                :key="'non-profit-' + f.key"
                :class="{'plan__features-item_unlimited': listOfFeatures[f].unlimited, 'plan__features-item_tooltip': listOfFeatures[f].tooltip, 'plan__features-item_tooltip-narrow': listOfFeatures[f].tooltipNarrow}"
              )
                InfiniteIcon.plan__features-item-infinite(v-if="listOfFeatures[f].unlimited")
                template {{ $t('pages.ssr_pricing_plans.plans.features.' + listOfFeatures[f].key + '.text') }}
                InfoCircle.plan__features-item-info(
                  v-if="listOfFeatures[f].tooltip"
                  :animated="false"
                  html
                  neat
                  multilined
                  narrow-icon
                  :autoResponsive="true"
                  :text="$t('pages.ssr_pricing_plans.plans.features.' + listOfFeatures[f].key + '.tooltip', { url: `https://help.rss.com/${$i18n.locale}/support/solutions/articles/44002307294-bandwidth-on-rss-com` })"
                )
              li.plan__features-item.plan__features-item_text {{ $t('pages.ssr_pricing_plans.plans.podcast_networks.features_text') }}

          RssUiButton.plan__button(
            wide
            rainbow
            :blank="false"
            :href="getStartedForFreeButtonLink"
          ) {{ getStartedForFreeButtonText }}

          span.plans__no-card {{ $t('pages.ssr_pricing_plans.plans.card_info') }}
</template>

<script>
import { mapState } from 'vuex'

import features from './list-of-features.js'

import MicSvg1 from './svg/MicSvg1'
import MicSvg2 from './svg/MicSvg2'
import HandIconSvg from './svg/HandIcon'
import InfiniteIcon from './svg/Infinite'

import InfoCircle from '@/components/shared/elements/info-circle/index-v2.vue'

import { config } from '@/config'

export default {
  components: {
    InfoCircle,

    MicSvg1,
    MicSvg2,
    HandIconSvg,
    InfiniteIcon
  },

  data () {
    return {
      proBillingAnnually: true,
      networksBillingAnnually: true,

      listOfFeatures: features,
      discountOfAnnualAllInOnePlan: 25,
      discountOfAnnualPodcastNetworksPlan: 25,
      nonProfitPlanFeatures: ['unlimited_episodes', 'unlimited_duration', 'audio_to_video', 'episode_transcripts', 'analytics', 'feed', 'website', 'embedded_player', 'scheduling', 'auto_submission', 'auto_distribution', 'apple_podcasts_subscription', 'media_sharing', 'support'],
      allInOnePlanFeatures: ['unlimited_episodes', 'unlimited_duration', 'audio_to_video', 'episode_transcripts', 'analytics', 'monetize', 'feed', 'website', 'embedded_player', 'scheduling', 'auto_submission', 'auto_distribution', 'apple_podcasts_subscription', 'media_sharing', 'pro_support'],
      enterprisePlanFeatures: ['unlimited_podcasts', 'unlimited_episodes', 'unlimited_duration', 'audio_to_video', 'episode_transcripts', 'analytics', 'monetize', 'feed', 'website', 'embedded_player', 'scheduling', 'auto_submission', 'auto_distribution', 'apple_podcasts_subscription', 'media_sharing', 'pro_support']
    }
  },

  computed: {
    ...mapState('auth', ['currentCountry', 'isLoggedIn']),

    allInOnePrice () {
      if (!this.proBillingAnnually) {
        return this.currencySymbol + ' 15.99&nbsp;'
      }
      return this.currencySymbol + ' 11.99&nbsp;'
    },

    podcastNetworksPrice () {
      if (!this.networksBillingAnnually) {
        return this.currencySymbol + ' 24.99&nbsp;'
      }
      return this.currencySymbol + ' 18.75&nbsp;'
    },

    dashboardAppUrl () {
      return config.dashboardAppUrl
    },

    currencySymbol () {
      if (this.currentCountry === 'US') {
        return '$'
      }
      return 'USD'
    },

    getStartedForFreeButtonLink () {
      if (this.isLoggedIn) {
        return this.$locatedLink('/', this.dashboardAppUrl)
      }
      return this.$locatedLink('/auth/sign-up/', this.dashboardAppUrl)
    },

    getStartedForFreeButtonText () {
      if (this.isLoggedIn) {
        return this.$t('homepage.hero.button_logged')
      }
      return this.$t('form.buttons.global__get_started_free')
    },

    computedDiscountAppliedAllInOneText () {
      return this
        .$t('pages.ssr_pricing_plans.plans.all_in_one.discount', { discount: `${this.discountOfAnnualAllInOnePlan}%` })
        .toString()
    },

    computedDiscountAppliedPodcastNetworksText () {
      return this
        .$t('pages.ssr_pricing_plans.plans.all_in_one.discount', { discount: `${this.discountOfAnnualPodcastNetworksPlan}%` })
        .toString()
    }
  },

  mounted () {
    if (this.$route.query.contact_us) {
      this.$router.push({ query: { contact_us: undefined } })
      this.openFreshdeskWidget()
    }
  },

  methods: {
    openFreshdeskWidget () {
      // eslint-disable-next-line
      FreshworksWidget('open', 'ticketForm')
    }
  }
}
</script>

<style lang="scss" scoped>
.lp-container {
  width: 1182px + $gap + $gap;
}

.plans {
  z-index: 10;
  position: relative;
  margin-bottom: 84px;

  &__wrapper {
    z-index: 5;
    display: grid;
    position: relative;
    grid-gap: $gap * 1.5;
    align-items: flex-start;
    grid-template-columns: 1fr 1fr 1fr;

    @include display-less(hd) {
      grid-gap: $gap;
    }

    @include display-less(big) {
      grid-gap: 1.5 * $gap;
      grid-template-columns: 1fr 1fr;
    }

    @include display-less(tablet) {
      grid-template-columns: 1fr;
    }
  }

  &__decoration {
    z-index: 3;
    width: 100%;
    bottom: -33px;
    height: 388px;
    position: absolute;
  }

  &__no-card {
    color: #fff;
    width: 100%;
    display: block;
    font-size: 16px;
    text-align: center;
    margin-top: $gap * 1.5;
  }
}

.plan {
  display: grid;
  position: relative;
  flex-direction: column;

  &_auto-height {
    display: flex;
  }

  &_empty-price {
    .plan__billing {
      padding-top: 0;
    }
  }

  &__discount {
    left: 50%;
    top: -32px;
    color: #fff;
    height: 32px;
    display: flex;
    font-size: 12px;
    font-weight: bold;
    position: absolute;
    padding-left: 20px;
    padding-right: 20px;
    align-items: center;
    white-space: nowrap;
    text-transform: uppercase;
    border-radius: 8px 8px 0 0;
    transform: translateX(-50%);
    background: linear-gradient(90deg, #6A68E6 0%, #C863EC 100%);
  }

  &__wrapper {
    display: flex;
    padding: $gap * 2;
    background: #160A28;
    border-radius: 20px;
    box-sizing: border-box;
    flex-direction: column;

    @include display-less(hd) {
      padding-left: $gap;
      padding-right: $gap;
    }

    @include display-less(tablet) {
      padding: $gap * 2;
    }

    @include display-less(phone) {
      padding: $gap;
    }
  }

  &:not(.plan_primary) {
    .plan__wrapper {
      border: 1px solid #2D223D;
    }
  }

  &_primary {
    padding: 3px;
    border-radius: 22px;
    background: linear-gradient(90deg, #F97901 0%, #E41ABA 100%);

    .plan__wrapper {
      padding-top: $gap * 2 - 3px;
      padding-bottom: $gap * 2 - 3px;
    }
  }

  &_first {
    @include display-less(tablet) {
      order: -1;
    }
  }

  &_free {
    margin-bottom: $gap * 2;
  }

  &__headline {
    display: flex;
    align-items: center;
    margin-bottom: $gap * 1.5;

    &-title {
      color: #fff;
      font-size: 26px;
      font-weight: bold;
      margin-left: 12px;

      @include display-less(phone) {
        font-size: 20px;
      }
    }
  }

  &__price {
    display: flex;
    min-height: 28px;
    align-items: center;
    margin-bottom: $gap * 1.5;

    &_annually {
      flex-wrap: wrap;
      row-gap: $gap * 0.5;

      .plan__price-duration {
        margin-right: $gap * 2;
      }

      .plan__price-annually {
        margin-left: 0;
      }
    }

    @media (max-width: 408px) {
      position: relative;

      &_annually {
        padding-bottom: $gap * 2.5;

        .plan__price-annually {
          display: block;
        }
      }
    }

    &-value {
      color: #fff;
      line-height: 1;
      font-size: 22px;
      font-weight: bold;

      &_stroked {
        opacity: 0.9;
        font-size: 16px;
        margin-right: 4px;
        line-height: 19px;
        text-decoration-line: line-through;
      }
    }

    &-duration {
      color: #fff;
      line-height: 1;
      font-size: 14px;
      margin-top: 6px;
    }

    &-annually {
      color: #fff;
      padding: 8px;
      line-height: 1;
      font-size: 12px;
      margin-left: 12px;
      border-radius: 2px;
      background: rgba(255, 255, 255, 0.07);

      @media (max-width: 408px) {
        left: 0;
        bottom: 0;
        display: none;
        margin-left: 0;
        position: absolute;
      }
    }
  }

  &__description {
    &-text {
      color: #fff;
      font-size: 14px;
      line-height: 150%;
      margin-bottom: $gap * 1.5;

      ::v-deep strong {
        color: #fff;
      }
    }
  }

  &__features {
    display: flex;
    position: relative;
    flex-direction: column;
    align-items: flex-start;
    padding-top: $gap * 1.5;

    &:after {
      top: 0;
      z-index: 2;
      content: '';
      height: 1px;
      display: block;
      left: -$gap * 2;
      right: -$gap * 2;
      position: absolute;
      background-color: #2D223D;

      @include display-less(hd) {
        left: -$gap;
        right: -$gap;
      }
    }

    &-item {
      color: #fff;
      position: relative;
      padding-left: 12px;
      display: flex;
      align-items: center;

      &:not(&_unlimited) {
        &:after {
          left: 0;
          top: 50%;
          z-index: 1;
          width: 4px;
          height: 4px;
          content: '';
          display: block;
          margin-top: -2px;
          position: absolute;
          border-radius: 4px;
          background-color: #fff;
        }
      }

      &_text {
        padding-left: 0;
        font-size: 16px;
        margin-top: 12px;
        font-weight: 400;
        line-height: 150%;

        &:after {
          display: none!important;
        }
      }

      &_title {
        display: block;
        padding-left: 0;
        font-size: 16px;
        font-weight: bold;

        &:after {
          display: none!important;
        }
      }

      &_unlimited {
        padding-left: 28px;

        .plan__features-item-infinite {
          left: 0;
          top: 50%;
          z-index: 1;
          width: 20px;
          height: 10px;
          margin-top: -5px;
          position: absolute;
        }
      }

      &_tooltip {
        cursor: pointer;
        padding-right: 20px;

        ::v-deep .tooltip-content {
          background-color: #2E1F42;
          bottom: calc(100% + 12px);

          @media screen and (max-width: 1230px) {
            width: 250px!important;
          }

          @include display-less(tablet) {
            width: 190px!important;
            font-size: 12px;
          }

          &:before {
            top: 100%;
            left: 50%;
            right: auto;
            bottom: auto;
            transform: translateX(-50%);
            border-top: 12px solid #2E1F42;
            border-top-color: #2E1F42!important;
            border-left: 12px solid transparent;
            border-right: 12px solid transparent;
          }
        }

        .plan__features-item-info {
          top: 50%;
          right: 0px;
          margin-top: -10px;
          position: absolute;
        }

        &-narrow {
          ::v-deep .tooltip-content {
            width: 200px!important;

            @media screen and (max-width: 1230px) {
              width: 200px!important;
            }

            @include display-less(tablet) {
              width: 190px!important;
              font-size: 12px;
            }
          }
        }

        &:hover {
          ::v-deep .tooltip-content {
            display: block!important;
          }
        }
      }

      &:not(:last-child) {
        margin-bottom: 12px;
      }
    }
  }

  &__billing {
    padding-top: 34px;
    position: relative;
    margin-bottom: $gap * 1.5;

    &-annual {
      top: 0;
      left: 0;
      font-size: 14px;
      min-height: 22px;
      font-weight: 700;
      position: absolute;
      margin-bottom: 12px;
      display: inline-block;
      text-transform: uppercase;

      @include display-less(phone) {
        line-height: 1;
      }
    }

    &-btn {
      margin-bottom: $gap * 1.5;
    }

    &-bottom {
      display: flex;
      align-items: center;
      margin-bottom: $gap * 1.5;
    }

    &-text {
      color: #fff;
      font-size: 14px;
    }

    ::v-deep .switch {
      width: 34px;
      height: 20px;
      margin-left: 12px;
      margin-right: 12px;

      .check {
        width: 34px;
        height: 20px;

        &:before {
          width: 14px;
          height: 14px;
        }
      }
    }
  }

  &__button {
    margin-top: auto;
    font-weight: bold;
  }
}
</style>
